import type { ReactNode } from 'react';

import type { CreateCheckDepositScanner } from '../Add';
import {
  type TimelineEntry,
  TimelinePartyType,
  TimelineStatus,
  type TransferCheckPayee,
} from '~/app/pages/Transfers/_components/Timeline/types';
import { buildTimelineEntryForTransferReviews, buildTransferParty } from '~/app/pages/Transfers/_utils/timeline';
import type { CheckReturnReason, CheckReturnsList, CheckTransfer } from '~/repositories';
import type { AccountNumber, BankAccount } from '~/repositories/BankAccountRepository';
import type { TransferBatch } from '~/typings/API';
import { formatString } from '~/util';
import { mergeAccountAndCheckNumber } from '~/util/getAccountAndCheckNumber';

const checkEvents = [
  {
    label: 'Created',
    field: 'createdAt',
  },
  {
    label: 'Pending Approval',
    field: 'preReviewAt',
  },
  {
    label: 'Deposited',
    field: 'depositedAt',
    status: TimelineStatus.Success,
  },
  {
    label: 'Pending deposit',
    field: 'PendingDepositAt',
  },
  {
    label: 'Pending stop',
    field: 'pendingStopAt',
    status: TimelineStatus.InProgress,
  },
  {
    label: 'Stopped',
    field: 'stoppedAt',
    status: TimelineStatus.Danger,
  },
  {
    label: 'Settled',
    field: 'settledAt',
  },
  {
    label: 'Pending first return',
    field: 'pendingFirstReturnAt',
    status: TimelineStatus.InProgress,
  },
  {
    label: 'First return',
    field: 'firstReturnAt',
  },
  {
    label: 'Reclear',
    field: 'reclearAt',
  },
  {
    label: 'Second return',
    field: 'secondReturnAt',
  },
  {
    label: 'Pending user initiated return',
    field: 'pendingUserInitiatedReturnAt',
    status: TimelineStatus.InProgress,
  },
  {
    label: 'User initiated returned',
    field: 'userInitiatedReturnedAt',
  },
  {
    label: 'Returned',
    field: 'returnedAt',
  },
];

export const buildCheckEvents = ({
  transfer,
  account,
  batch: batchArg,
  images,
  previewPDFAction,
}: {
  transfer: CheckTransfer;
  account: {
    accountNumber: AccountNumber;
    bankAccount: BankAccount;
    entityType?: 'person' | 'business';
  };
  batch?: TransferBatch;
  images?: {
    frontImage?: string;
    backImage?: string;
  };
  previewPDFAction?: ReactNode;
}): TimelineEntry[] => {
  const isRejected = transfer.status === 'rejected';
  const isStopped = transfer.status === 'stopped';
  const isReturned = transfer.status.includes('return');

  return checkEvents
    .filter(
      (event) =>
        event.field === 'createdAt' ||
        event.field === 'settledAt' ||
        !!transfer[event.field as keyof typeof transfer] ||
        (event.field === 'preReviewAt' && !!transfer.reviews?.length)
    )
    .map((event) => {
      if (event.field === 'preReviewAt') {
        return buildTimelineEntryForTransferReviews(transfer.preReviewAt ?? undefined, transfer.reviews);
      }

      const date = (transfer[event.field as keyof typeof transfer] as string | undefined)
        ? new Date(transfer[event.field as keyof typeof transfer] as string)
        : undefined;

      const isIncoming = transfer.type.toLowerCase() === 'credit';

      let batch;
      let partyData;
      if (!isIncoming) {
        if (event.field === 'createdAt') {
          partyData = buildTransferParty({
            account,
            isIncoming: false,
            isOriginator: true,
          });
        } else if (event.field === 'settledAt') {
          partyData = {
            data: {
              payeeName: transfer.payeeName || transfer.extractedPayeeName,
              payeeAddress: transfer.payeeAddress,
              deliveredByColumn: transfer.deliveredByColumn,
              deliveryMethod: transfer.deliveryMethod,
              deliveryTrackingNumber: transfer.deliveryTrackingNumber,
              deliveryTrackingUrl: transfer.deliveryTrackingUrl,
              deliveryStatus: transfer.deliveryStatus,
              frontImage: images?.frontImage,
              backImage: images?.backImage,
              previewPDFAction,
            } as TransferCheckPayee,
            partyType: TimelinePartyType.Receiver,
          };
        }
      } else {
        if (event.field === 'createdAt') {
          partyData = {
            data: {
              payeeName: transfer.payeeName || transfer.description,
              deliveredByColumn: Boolean(transfer.deliveredByColumn),
              payeeAddress: transfer.payeeAddress,
              deliveryMethod: transfer.deliveryMethod,
              deliveryTrackingNumber: transfer.deliveryTrackingNumber,
              deliveryTrackingUrl: transfer.deliveryTrackingUrl,
              deliveryStatus: transfer.deliveryStatus,
            } as TransferCheckPayee,
            partyType: TimelinePartyType.Receiver,
          };
        } else if (event.field === 'settledAt') {
          batch = batchArg;
          partyData = buildTransferParty({
            account,
            checkImages: images,
            isIncoming: true,
            isOriginator: false,
          });
        }
      }

      const defaultStatus = isRejected || isStopped || isReturned ? TimelineStatus.Danger : TimelineStatus.Pending;
      let status = !!transfer[event.field as keyof typeof transfer] ? event.status : defaultStatus;
      if (event.field === 'pendingStopAt' && !!transfer.stoppedAt) {
        status = TimelineStatus.Success;
      }
      if (event.field === 'pendingFirstReturnAt' && !!transfer.firstReturnAt) {
        status = TimelineStatus.Success;
      }
      if (event.field === 'pendingUserInitiatedReturnAt' && !!transfer.userInitiatedReturnedAt) {
        status = TimelineStatus.Success;
      }

      const amount = isIncoming ? transfer.depositedAmount : transfer.positivePayAmount || transfer.depositedAmount;

      return {
        label: event.label,
        amount: amount
          ? {
              value: parseFloat(amount),
              isAdding: event.field === 'settledAt' || event.field === 'depositedAt',
              currencyCode: 'USD',
            }
          : undefined,
        batch,
        date,
        partyData,
        status,
      };
    });
};

export const buildCheckReturnEvents = (returns?: CheckReturnsList['returns']): TimelineEntry[] => {
  if (!returns || returns.length < 1) return [];

  return returns.map((checkReturn) => {
    return {
      label: `Return ${formatString(checkReturn.status)}`,
      date: new Date(checkReturn.createdAt),
      status: checkReturn.status === 'processed' ? TimelineStatus.Returned : TimelineStatus.Success,
      returnData: {
        reason: checkReturn.returnReason as CheckReturnReason,
      },
    } as TimelineEntry;
  });
};

export const mergeCheckDepositNumbers = (data: Partial<CreateCheckDepositScanner>) => {
  const { accountNumber, checkNumber, ...rest } = data;
  const { auxiliaryOnUs, onUs } = mergeAccountAndCheckNumber(
    rest.micrLine?.onUs ?? '',
    rest.micrLine?.auxiliaryOnUs ?? '',
    accountNumber ?? '',
    checkNumber ?? ''
  );

  rest.micrLine!.auxiliaryOnUs = auxiliaryOnUs;
  rest.micrLine!.onUs = onUs;

  return rest;
};
